import type { Direction } from '../sim/direction';
import { directionFromKeys, runningFromKeys } from './keymap';

export interface ManualRunningInput {
  dungeon: boolean;
  alwaysRun: boolean;
  keyboard: boolean;
  keyboardRunning: boolean;
  joystick: boolean;
  mouseRunning: boolean;
}

/**
 * Resolve the gait for the manual input source selected by `main.ts`.
 * Dungeon action movement promotes keyboard steering only, leaving the mouse and
 * mobile joystick on their existing source-specific rules.
 */
export function manualRunning(input: ManualRunningInput): boolean {
  if (input.alwaysRun) return true;
  if (input.keyboard) return input.dungeon || input.keyboardRunning;
  if (input.joystick) return false;
  return input.mouseRunning;
}

/**
 * The live set of held movement/run key codes (WASD / arrows + Shift), behind a tiny
 * controller so the movement-input contract - and crucially the "release everything on
 * focus loss" rule - is unit-testable away from `main.ts`'s DOM/Pixi wiring, which is
 * otherwise browser-verified only (see `vite.config` - the render layer is not unit-tested).
 *
 * `keys` is only ever emptied by {@link release} (the matching keyup) or {@link clear}
 * (the blur / pagehide / tab-hide focus-loss sweep `main.ts` wires up). A focus-stealing
 * key combo (alt-tab, ctrl-w, cmd+key on macOS, ...) can swallow a keyup entirely -
 * stranding its code here so the character walks/runs on with nothing physically held -
 * so the focus-loss {@link clear} is what stops that phantom movement. The pure
 * key-set -> facing mapping lives in {@link directionFromKeys}; this only owns the set.
 */
export class HeldMovement {
  /** Live held-code set; the ticker samples it each frame via {@link direction}/{@link running}. */
  readonly keys = new Set<string>();

  /** Record a movement/run keydown. */
  press(code: string): void {
    this.keys.add(code);
  }

  /** Record a matching keyup. */
  release(code: string): void {
    this.keys.delete(code);
  }

  /** Drop every held key - the focus-loss "release everything" sweep. */
  clear(): void {
    this.keys.clear();
  }

  /** The 8-way facing the held keys resolve to, or null when idle / cancelled. */
  direction(): Direction | null {
    return directionFromKeys(this.keys);
  }

  /** Whether the current movement mode should use the run gait. */
  running(runByDefault = false): boolean {
    return runByDefault || runningFromKeys(this.keys);
  }
}
